import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ErroDaApi, chamarApi } from '../api/cliente.ts';
import { CampoTexto } from './Campo.tsx';
import { Botao, Cartao, Pilula } from './Superficie.tsx';

export type ItemDoCadastro = {
  /** O que vai na rota: o id da operadora, o código da origem. */
  id: string;
  nome: string;
  ativa: boolean;
  usos: number;
  /** O sistema depende dele: renomeia, mas não desativa nem exclui. */
  travado?: boolean;
};

/** Os nomes que mudam de um cadastro para o outro. As duas palavras são femininas. */
export type VocabularioDoCadastro = {
  /** "operadora", "origem" — também o sufixo do `data-teste` de cada linha. */
  singular: string;
  /** "operadoras", "origens" — a rota, a chave da consulta e o `data-teste`. */
  plural: string;
  /** Em que os usos se contam: "registro" e "registros", "Lead" e "Leads". */
  uso: [singular: string, plural: string];
  /** O mesmo teto do esquema da API, para o campo não aceitar o que ela recusa. */
  tamanhoMaximo: number;
};

/**
 * Um cadastro de Configurações que se mantém pelo nome (NEX-228): incluir,
 * renomear, desativar ou reativar, e excluir.
 *
 * Excluir apaga só o que ninguém usa. Em uso, vira desativar: sai das opções e
 * continua no histórico. A tela diz qual dos dois vai acontecer antes do clique.
 */
export function CadastroPorNome({
  titulo,
  descricao,
  vocabulario,
  itens,
  pronto,
}: {
  titulo: string;
  descricao: string;
  vocabulario: VocabularioDoCadastro;
  itens: ItemDoCadastro[];
  pronto: boolean;
}): React.JSX.Element {
  const { singular, plural, tamanhoMaximo } = vocabulario;
  const clienteDeConsultas = useQueryClient();
  const [nova, definirNova] = useState('');
  const [aviso, definirAviso] = useState<string | null>(null);

  const recarregar = (): Promise<void> =>
    clienteDeConsultas.invalidateQueries({ queryKey: [plural] });

  const criar = useMutation({
    mutationFn: () => chamarApi(`/${plural}`, { metodo: 'POST', corpo: { nome: nova } }),
    onSuccess: async () => {
      definirAviso(`"${nova.trim()}" cadastrada.`);
      definirNova('');
      await recarregar();
    },
  });

  return (
    <Cartao titulo={titulo} descricao={descricao}>
      {/* A lista e o formulário juntos: Configurações tem mais de um "Incluir". */}
      <div data-teste={`cadastro-${plural}`}>
        {itens.map((item) => (
          <LinhaDoCadastro
            key={item.id}
            item={item}
            vocabulario={vocabulario}
            aoMudar={async (mensagem) => {
              definirAviso(mensagem);
              await recarregar();
            }}
          />
        ))}
        {pronto && itens.length === 0 ? (
          <p className="text-texto-suave">Nenhuma {singular} cadastrada.</p>
        ) : null}

        <form
          className="mt-4 flex flex-wrap items-end gap-2"
          onSubmit={(evento) => {
            evento.preventDefault();
            definirAviso(null);
            if (nova.trim().length >= 2) criar.mutate();
          }}
        >
          <CampoTexto
            rotulo={`Nova ${singular}`}
            name="novoNome"
            value={nova}
            maxLength={tamanhoMaximo}
            onChange={(evento) => definirNova(evento.target.value)}
            className="min-w-48 flex-1"
          />
          <Botao tipo="submit" variante="primario" desabilitado={criar.isPending}>
            Incluir
          </Botao>
        </form>
      </div>

      {criar.error ? (
        <p role="alert" className="mt-2 text-sm text-erro">
          {criar.error instanceof ErroDaApi
            ? criar.error.message
            : `Não foi possível cadastrar a ${singular}.`}
        </p>
      ) : null}
      {aviso ? (
        <p role="status" data-teste={`aviso-${plural}`} className="mt-2 text-sm text-sucesso">
          {aviso}
        </p>
      ) : null}
    </Cartao>
  );
}

/** Um item, com renomear, desativar ou reativar, e excluir. */
function LinhaDoCadastro({
  item,
  vocabulario,
  aoMudar,
}: {
  item: ItemDoCadastro;
  vocabulario: VocabularioDoCadastro;
  aoMudar: (mensagem: string) => Promise<void>;
}): React.JSX.Element {
  const { singular, plural, uso, tamanhoMaximo } = vocabulario;
  const emUso = `${item.usos} ${item.usos === 1 ? uso[0] : uso[1]}`;
  const [editando, definirEditando] = useState(false);
  const [nome, definirNome] = useState(item.nome);
  // Excluir pede um segundo clique: o primeiro só diz o que vai acontecer.
  const [confirmandoExclusao, definirConfirmandoExclusao] = useState(false);

  const atualizar = useMutation({
    mutationFn: (corpo: { nome?: string; ativa?: boolean }) =>
      chamarApi(`/${plural}/${item.id}`, { metodo: 'PATCH', corpo }),
    onSuccess: async (_resposta, corpo) => {
      definirEditando(false);
      await aoMudar(
        corpo.nome !== undefined
          ? `"${item.nome}" agora é "${corpo.nome.trim()}"${item.usos > 0 ? `, também nos ${item.usos} ${uso[1]} que a usavam` : ''}.`
          : corpo.ativa
            ? `"${item.nome}" reativada.`
            : `"${item.nome}" desativada: sai das opções e continua no histórico.`,
      );
    },
  });

  const excluir = useMutation({
    mutationFn: () =>
      chamarApi<{ excluida: boolean; usos: number }>(`/${plural}/${item.id}`, {
        metodo: 'DELETE',
      }),
    onSuccess: async (resultado) => {
      definirConfirmandoExclusao(false);
      await aoMudar(
        resultado.excluida
          ? `"${item.nome}" excluída.`
          : `"${item.nome}" está em ${resultado.usos} ${resultado.usos === 1 ? uso[0] : uso[1]}: foi desativada em vez de excluída.`,
      );
    },
  });

  const erro = atualizar.error ?? excluir.error;

  return (
    <div
      data-teste={`linha-${singular}`}
      className="flex flex-wrap items-center justify-between gap-2 border-b border-linha py-3 last:border-0"
    >
      {editando ? (
        <form
          className="flex flex-1 flex-wrap items-end gap-2"
          onSubmit={(evento) => {
            evento.preventDefault();
            if (nome.trim().length >= 2 && nome.trim() !== item.nome) {
              atualizar.mutate({ nome });
            } else {
              definirEditando(false);
            }
          }}
        >
          <CampoTexto
            rotulo={`Renomear ${item.nome}`}
            name="nome"
            value={nome}
            maxLength={tamanhoMaximo}
            onChange={(evento) => definirNome(evento.target.value)}
            className="min-w-48 flex-1"
            dica={
              item.usos > 0
                ? `O nome novo vai também para os ${item.usos} ${uso[1]} que usam este.`
                : undefined
            }
          />
          <Botao tipo="submit" variante="primario" tamanho="mini" desabilitado={atualizar.isPending}>
            Salvar
          </Botao>
          <Botao
            tamanho="mini"
            onClick={() => {
              definirNome(item.nome);
              definirEditando(false);
            }}
          >
            Cancelar
          </Botao>
        </form>
      ) : (
        <>
          <div className="min-w-0">
            <p className="truncate font-medium text-marinho">{item.nome}</p>
            <p className="text-xs text-texto-suave">
              {item.usos === 0 ? 'Sem uso' : `Em ${emUso}`}
              {item.travado ? ' · Usada pelo sistema: só o nome muda' : ''}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <Pilula tom={item.travado ? 'informacao' : item.ativa ? 'sucesso' : 'neutro'}>
              {item.travado ? 'Do sistema' : item.ativa ? 'Ativa' : 'Desativada'}
            </Pilula>
            {confirmandoExclusao ? (
              <>
                <span className="text-xs text-texto-suave">
                  {item.usos > 0
                    ? 'Em uso: vai ser desativada, não apagada.'
                    : 'Sem uso: vai ser apagada.'}
                </span>
                <Botao tamanho="mini" desabilitado={excluir.isPending} onClick={() => excluir.mutate()}>
                  Confirmar
                </Botao>
                <Botao tamanho="mini" onClick={() => definirConfirmandoExclusao(false)}>
                  Voltar
                </Botao>
              </>
            ) : (
              <>
                <Botao tamanho="mini" onClick={() => definirEditando(true)}>
                  Editar
                </Botao>
                {item.travado ? null : (
                  <>
                    <Botao
                      tamanho="mini"
                      desabilitado={atualizar.isPending}
                      onClick={() => atualizar.mutate({ ativa: !item.ativa })}
                    >
                      {item.ativa ? 'Desativar' : 'Reativar'}
                    </Botao>
                    <Botao tamanho="mini" onClick={() => definirConfirmandoExclusao(true)}>
                      Excluir
                    </Botao>
                  </>
                )}
              </>
            )}
          </div>
        </>
      )}
      {erro ? (
        <p role="alert" className="basis-full text-sm text-erro">
          {erro instanceof ErroDaApi ? erro.message : `Não foi possível alterar a ${singular}.`}
        </p>
      ) : null}
    </div>
  );
}
